import React from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useMenu } from '../contexts/MenuContext';
import { useNavigate } from 'react-router-dom';

interface CartProps {
  onClose: () => void;
}

const Cart: React.FC<CartProps> = ({ onClose }) => {
  const { cart, updateCartItem, removeFromCart, clearCart, getCartTotal, getCartItemCount } = useMenu();
  const navigate = useNavigate();

  const handleQuantityChange = (itemId: string, newQuantity: number) => {
    const item = cart.find(cartItem => cartItem.id === itemId);
    if (item) {
      updateCartItem(itemId, newQuantity, item.selectedExtras, item.notes);
    }
  };

  const handleExtraQuantityChange = (itemId: string, extraId: string, newQuantity: number) => {
    const item = cart.find(cartItem => cartItem.id === itemId);
    if (item) {
      const updatedExtras = item.selectedExtras.map(extra => 
        extra.extra.id === extraId ? { ...extra, quantity: newQuantity } : extra
      ).filter(extra => extra.quantity > 0);
      
      updateCartItem(itemId, item.quantity, updatedExtras, item.notes);
    }
  };

  const getItemTotal = (item: any) => {
    const itemTotal = item.menuItem.price * item.quantity;
    const extrasTotal = item.selectedExtras.reduce((total: number, extra: any) => 
      total + (extra.extra.price * extra.quantity), 0
    );
    return itemTotal + extrasTotal;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex-1"></div>
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold flex items-center">
            <ShoppingBag className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
            <span className="hidden sm:inline">Your Cart</span>
            <span className="sm:hidden">Cart</span>
            <span className="ml-1">({getCartItemCount()})</span>
          </h2>
          <div className="flex-1 flex justify-end">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {cart.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <ShoppingBag className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-500 mb-2">Your cart is empty</h3>
              <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">Add some delicious items to get started!</p>
              <button
                onClick={onClose}
                className="btn btn-primary btn-sm sm:btn-md"
              >
                Browse Menu
              </button>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {cart.map((item) => (
                <div key={item.id} className="border border-gray-200 rounded-lg p-3 sm:p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 pr-2">
                      <h4 className="font-semibold text-base sm:text-lg">{item.menuItem.name}</h4>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="text-gray-600 hover:text-gray-800 transition-colors p-1 flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Selected Extras */}
                  {item.selectedExtras.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Extras:</p>
                      <div className="space-y-1">
                        {item.selectedExtras.map((extra) => (
                          <div key={extra.extra.id} className="flex justify-between items-center text-xs sm:text-sm">
                            <span className="text-gray-600 flex-1 pr-2">
                              {extra.extra.name} (x{extra.quantity})
                            </span>
                            <div className="flex items-center space-x-1 sm:space-x-2">
                              <button
                                onClick={() => handleExtraQuantityChange(item.id, extra.extra.id, extra.quantity - 1)}
                                className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-5 sm:w-6 text-center text-xs sm:text-sm">{extra.quantity}</span>
                              <button
                                onClick={() => handleExtraQuantityChange(item.id, extra.extra.id, extra.quantity + 1)}
                                className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                              <span className="text-gray-800 font-semibold ml-1 sm:ml-2 text-xs sm:text-sm">
                                +£{(extra.extra.price * extra.quantity).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {item.notes && (
                    <div className="mb-3">
                      <p className="text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes:</p>
                      <p className="text-xs sm:text-sm text-gray-600 italic">"{item.notes}"</p>
                    </div>
                  )}

                  {/* Quantity and Price */}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                        className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                      >
                        <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                      <span className="w-6 sm:w-8 text-center font-semibold text-sm sm:text-base">{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                        className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                      >
                        <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                    <div className="text-right">
                      <p className="text-sm sm:text-base font-bold text-gray-800">
                        £{getItemTotal(item).toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="border-t border-gray-200 p-4 sm:p-6">
            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <span className="text-lg sm:text-xl font-bold">Total:</span>
              <span className="text-lg sm:text-xl font-bold text-gray-800">£{getCartTotal().toFixed(2)}</span>
            </div>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => {
                  onClose();
                  // Small delay to allow modal close animation
                  setTimeout(() => navigate('/menu'), 100);
                }}
                className="btn btn-outline btn-sm px-6 py-2 flex-1"
              >
                Back to Menu
              </button>
              <button
                onClick={() => {
                  onClose();
                  // Small delay to allow modal close animation
                  setTimeout(() => navigate('/order'), 100);
                }}
                className="btn btn-primary btn-sm px-6 py-2 flex-1"
              >
                Proceed to Order
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Cart;

